import { makeStyles } from 'tss-react/mui';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import {
  CartItem,
  flatInvalidModGroupsWithDescriptions,
} from '../../../utils/cart';
import {
  checkForUnavailableRequiredModifierGroup,
  checkItemInCartItemTree,
  isItem86edToday,
  splitPathToItem,
} from '../../../utils/menu';
import MultipleSelectionModGroup from './MultipleSelectionModGroup';
import SingularSelectionModGroup from './SingularSelectionModGroup';
import Delayed from './Delayed';
import { ParsedModifierGroup } from '../../../types/menu';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import { useSelector } from 'react-redux';
import { selectDialogHighlightedModifier } from '../../../selectors/dialog';
import { selectIsAIAutoMode } from '../../../redux/features/ai/ai.selector';
import Colors from '../../../utils/color';
import { selectUnavailableItemsByName } from '../../../selectors/menu';
import { selectIsHitl7CenterPaneEnabled } from '../../../redux/features/config/config.selector';
import useTTSMessage from '../../../hooks/useTTSMessage.hooks';
import { SelectMenuItemModifier } from './modGroup.props';
import {
  applyModGroupDefaults,
  getLinkedSelectionsKey,
} from './modGroup.utils';
import { getOptionModifierGroups } from '../../../utils/lazyOptionBuild';

interface OwnProps {
  modGroup: ParsedModifierGroup;
  cartItem: CartItem;
  selectMenuItemModifier: SelectMenuItemModifier;
  pathToItem: string;
  isSelectedToFocus?: boolean; // Selected by Tab key
  isHighlighted?: boolean; // Highlighted in Omnibar search result
}

interface MemoProps extends OwnProps {
  // HITL-1698: see getLinkedSelectionsKey. Read only by the memo comparator.
  linkedSelectionsKey: string;
}

export interface IModGroupStyleProps {
  isSelectedToFocus?: boolean;
}

const useStyles = makeStyles<IModGroupStyleProps>()(
  ({ spacing }, { isSelectedToFocus }) => {
    const bgColor = isSelectedToFocus ? Colors.alto : Colors.white;
    return {
      modGroupClass: {
        marginLeft: spacing(1.5),
        backgroundColor: bgColor,
      },
      // HITL-1616: the row-and-drawer pane carries its own depth indent
      // (`MOD_GROUP_ROW_INDENT_PX`), so the 12px this class adds at EVERY
      // nesting level is a second, compounding one. Pulled back to 4px a side.
      // Net effect on width is +4px (left loses 8, right gains 4), and the
      // right margin now compounds with depth where before there was none.
      // Gated, so the chip grid keeps its original 12px and no right margin.
      modGroupCompact: {
        marginLeft: spacing(0.5),
        marginRight: spacing(0.5),
      },
    };
  }
);

const ModGroup = (props: MemoProps) => {
  const modGroupRef = useRef<HTMLDivElement | null>(null);
  const {
    modGroup,
    cartItem,
    selectMenuItemModifier,
    pathToItem,
    isSelectedToFocus,
  } = props;

  const isBrandMenu = getMenuUnificationFeatureFlag();
  const highlightedModifier = useSelector(selectDialogHighlightedModifier);
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  const isAIAutoMode = useSelector(selectIsAIAutoMode);
  const isCenterPaneEnabled = useSelector(selectIsHitl7CenterPaneEnabled);
  const { sendModGroupTTSMessage, sendTTSMessage } = useTTSMessage();

  const isHighlighted = highlightedModifier === pathToItem;
  let parentModifier = '';
  const nodesInPath = splitPathToItem(pathToItem);
  if (nodesInPath.length > 2) {
    parentModifier = nodesInPath[nodesInPath.length - 2];
  }

  const { classes, cx } = useStyles({
    isSelectedToFocus,
  });

  useEffect(() => {
    if (isSelectedToFocus || isHighlighted) {
      modGroupRef.current?.scrollIntoView();
    }
  }, [isSelectedToFocus, isHighlighted]);

  const allModifierUnavailable = !Object.values(modGroup.menuItems).find(
    (menuItem) => {
      if (
        (menuItem.available || isItem86edToday(menuItem)) &&
        !checkForUnavailableRequiredModifierGroup(
          getOptionModifierGroups(menuItem)
        )
      ) {
        return menuItem;
      }
    }
  );
  const cartModGroup = checkItemInCartItemTree({ cartItem, pathToItem });

  // HITL-1433: a required group can arrive unmet from an escalation handoff
  // (the AI's "?" placeholder doesn't resolve to a menu option) and render
  // below the fold of a tall item card, so the agent never sees the red "!"
  // label without scrolling. Reveal the item's first such group. Groups with
  // defaults are excluded on both sides of the comparison: their selections
  // apply asynchronously right after the mount wave, so they are only
  // transiently unmet and must not swallow the reveal of a group that
  // actually needs the agent.
  //
  // The reveal is evaluated when the agent GAINS control (isAIAutoMode
  // true→false), not at mount: hypothesis frames only rebuild the cart while
  // AI auto mode is on (useHypothesisFrames), so hypothesis-mounted groups
  // always mount under auto mode — and revealing during auto mode would yank
  // the monitoring agent's view on every rebuild. The same effect also covers
  // groups that mount while the agent is already in control (manual adds,
  // nested groups appearing after a selection), since it runs on mount too.
  //
  // The scroll goes through the ref callback when the DOM node doesn't exist
  // yet: top-level groups render behind <Delayed>.
  const autoRevealEvaluatedRef = useRef(false);
  const pendingAutoRevealRef = useRef(false);
  const setModGroupContentRef = useCallback((node: HTMLDivElement | null) => {
    modGroupRef.current = node;
    if (node && pendingAutoRevealRef.current) {
      pendingAutoRevealRef.current = false;
      node.scrollIntoView?.();
    }
  }, []);
  useEffect(() => {
    if (isAIAutoMode) {
      // AI (re)gained control — cancel any pending reveal and allow one
      // evaluation in the next agent-control period.
      autoRevealEvaluatedRef.current = false;
      pendingAutoRevealRef.current = false;
      return;
    }
    if (autoRevealEvaluatedRef.current) return;
    autoRevealEvaluatedRef.current = true;
    const selectedCount = Object.keys(cartModGroup?.selectedItems || {}).length;
    const isFirstUnmetRequiredGroup =
      !modGroup.defaultSelectedItemIds?.length &&
      selectedCount < modGroup.minimumSelections &&
      flatInvalidModGroupsWithDescriptions(cartItem).modGroups.find(
        (group) => !group.defaultSelectedItemIds?.length
      )?.id === modGroup.id;
    if (!isFirstUnmetRequiredGroup) return;
    if (modGroupRef.current) {
      modGroupRef.current.scrollIntoView?.();
    } else {
      pendingAutoRevealRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAIAutoMode]);

  useEffect(() => {
    // handle default modifier only when it doesn't exit in the cartModGroup
    if (!cartModGroup) {
      applyModGroupDefaults({
        cartItem,
        modGroup,
        pathToItem,
        parentModifier,
        isBrandMenu,
        unavailableItemsByName,
        selectMenuItemModifier,
      });
    }
  }, [cartModGroup, pathToItem]);

  const isSingleChoice =
    modGroup.minimumSelections === modGroup.maximumSelections &&
    modGroup.maximumSelections === 1;

  const handleModChange = useCallback(
    (innerModGroup: ParsedModifierGroup) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const itemId = event.target.value;
        const isChecked = isSingleChoice || event.target.checked;
        if (itemId.length > 0) {
          selectMenuItemModifier(
            innerModGroup.menuItems[itemId],
            innerModGroup,
            pathToItem,
            isChecked,
            true,
            false
          );
        }
      },
    [isSingleChoice, pathToItem, selectMenuItemModifier]
  );

  const modGroupProps = {
    cartItem,
    key: `${modGroup.id}_main_node`,
    modGroup,
    cartModGroup,
    onChange: handleModChange,
    selectMenuItemModifier,
    isSelectedToFocus,
    isHighlighted,
    parentModifier: isBrandMenu ? parentModifier : '',
    sendModGroupTTSMessage,
    sendTTSMessage,
  };

  const content = (
    <div
      data-testid="mod-group"
      className={cx(
        classes.modGroupClass,
        isCenterPaneEnabled && classes.modGroupCompact
      )}
      ref={setModGroupContentRef}
    >
      {allModifierUnavailable ? null : (
        <>
          {isSingleChoice ? (
            <SingularSelectionModGroup
              {...modGroupProps}
              pathToItem={pathToItem}
            />
          ) : (
            <MultipleSelectionModGroup
              {...modGroupProps}
              pathToItem={pathToItem}
            />
          )}
        </>
      )}
    </div>
  );

  // The Delayed reveal exists to keep the item's initial mount from blocking
  // first paint (HITL-720). Nested groups mount one at a time when the agent
  // selects their parent option — small, render-scoped work — so the fixed
  // delay there is pure latency on every nested expand (HITL-1422). Top-level
  // groups (single-segment path) keep the deferred reveal.
  const isNestedGroup = nodesInPath.length > 1;
  return isNestedGroup ? content : <Delayed>{content}</Delayed>;
};

// Re-render a mod group only when ITS slice of the cart changed. The cart
// item object gets a new identity on every cart edit, which would otherwise
// re-render every mounted group (a crowd pleaser mounts dozens) on each
// click. Immer's copy-on-write guarantees the node returned by
// checkItemInCartItemTree changes reference iff something inside this
// group's subtree was modified, so comparing node references is exact
// (HITL-1422).
//
// The one thing outside that subtree a group reads is which of its options
// the brand menu currently allows, and that can hang off a sibling group's
// pick (Drink Size off Drink). linkedSelectionsKey carries those picks, so a
// change there re-renders the group too (HITL-1698).
const areModGroupPropsEqual = (prev: MemoProps, next: MemoProps): boolean => {
  if (
    prev.linkedSelectionsKey !== next.linkedSelectionsKey ||
    prev.modGroup !== next.modGroup ||
    prev.pathToItem !== next.pathToItem ||
    prev.selectMenuItemModifier !== next.selectMenuItemModifier ||
    prev.isSelectedToFocus !== next.isSelectedToFocus ||
    prev.isHighlighted !== next.isHighlighted
  ) {
    return false;
  }
  if (prev.cartItem === next.cartItem) {
    return true;
  }
  if (prev.cartItem.cartItemId !== next.cartItem.cartItemId) {
    return false;
  }
  // Normalize the miss value: the tree walk returns null when the cart has no
  // child groups at all yet, but undefined once any group exists — both mean
  // "this group has no selections", and treating them as different would
  // re-render every group on the first selection.
  const prevNode =
    checkItemInCartItemTree({
      cartItem: prev.cartItem,
      pathToItem: prev.pathToItem,
    }) ?? null;
  const nextNode =
    checkItemInCartItemTree({
      cartItem: next.cartItem,
      pathToItem: next.pathToItem,
    }) ?? null;
  return prevNode === nextNode;
};

const MemoModGroup = memo(ModGroup, areModGroupPropsEqual);

// Unmemoised on purpose: it runs on every parent render so the key is worked
// out from the current cart before the comparator decides.
const ModGroupWithLinkedSelections = (props: OwnProps) => {
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  const linkedSelectionsKey = getMenuUnificationFeatureFlag()
    ? getLinkedSelectionsKey({
        cartItem: props.cartItem,
        pathToItem: props.pathToItem,
        modGroupName: props.modGroup.name,
        unavailableItemsByName,
      })
    : '';
  return <MemoModGroup {...props} linkedSelectionsKey={linkedSelectionsKey} />;
};

export default ModGroupWithLinkedSelections;
